import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { SpeakerVettingJSON } from '../lib/openai';

export interface SpeakerVettingRecord {
  id: string;
  title: string;
  content: SpeakerVettingJSON;
  created_at: string;
  updated_at: string;
}

interface CreateSpeakerVettingData {
  title: string;
  content: SpeakerVettingJSON;
}

export function useSpeakerVetting() {
  const [analyses, setAnalyses] = useState<SpeakerVettingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('speaker_vetting_analyses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnalyses(data || []);
    } catch (err) {
      console.error('Error loading speaker vetting analyses:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalyses();
  }, []);

  const saveAnalysis = async (data: CreateSpeakerVettingData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: saved, error: saveError } = await supabase
      .from('speaker_vetting_analyses')
      .insert([{ ...data, user_id: user.id }])
      .select()
      .single();

    if (saveError) throw saveError;
    await loadAnalyses();
    return saved as SpeakerVettingRecord;
  };

  const deleteAnalysis = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('speaker_vetting_analyses')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    await loadAnalyses();
  };

  return {
    analyses,
    loading,
    error,
    saveAnalysis,
    deleteAnalysis,
    refresh: loadAnalyses,
  };
}
